/**
 * Lane 1 — questions needing response (spec #44 §1.2).
 *
 * A union of three sources with one honest ordering (newest first) and one
 * count: open tier-2 proposals, plan approvals, and the two human hand-off
 * types. Zero of them is a POSITIVE state ("Nothing needs you"), not a
 * failure and not a blank.
 */

import { useState } from 'react';
import { SkeletonBodyText, Text } from '@shopify/polaris';

import { useWorkspace } from '../../hooks/useWorkspace';
import type { HandoffOutcome } from '../../types/handoffs';
import type { WorkspaceQuestion } from '../../types/workspace';
import { HandoffCard } from './HandoffCard';
import { PlanApprovalCard } from './PlanApprovalCard';
import { QuestionCard } from './QuestionCard';
import { WorkspaceEmpty } from './WorkspaceEmpty';
import styles from './workspace.module.css';

export function QuestionsLane() {
  const {
    questions,
    agents,
    loading,
    errors,
    approveQuestion,
    declineQuestion,
    resolveQuestion,
  } = useWorkspace();
  const [busyId, setBusyId] = useState<string | null>(null);

  const agentNames: Record<string, string> = {};
  agents.forEach((a) => {
    agentNames[a.domain] = a.display_name;
  });

  const run = async (
    question: WorkspaceQuestion,
    action: () => Promise<void>
  ) => {
    setBusyId(question.id);
    try {
      await action();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={styles.lane} aria-label="Questions needing response">
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>Questions needing response</span>
        <span className={styles.mono} data-testid="questions-lane-count">
          {questions.length}
        </span>
      </div>
      <div className={styles.laneCards}>
        {errors.questions ? (
          <WorkspaceEmpty kind="lane-error" reason={errors.questions} />
        ) : loading.questions && questions.length === 0 ? (
          <SkeletonBodyText lines={4} />
        ) : questions.length === 0 ? (
          <WorkspaceEmpty kind="no-questions" />
        ) : (
          questions.map((question) => {
            const busy = busyId === question.id;
            if (question.source === 'handoff') {
              return (
                <HandoffCard
                  key={question.id}
                  question={question}
                  agentNames={agentNames}
                  busy={busy}
                  onResolve={(q, outcome: HandoffOutcome, reason) =>
                    void run(q, () => resolveQuestion(q, outcome, reason))
                  }
                />
              );
            }
            if (question.source === 'plan_approval') {
              return (
                <PlanApprovalCard
                  key={question.id}
                  question={question}
                  agentNames={agentNames}
                  busy={busy}
                  onApprove={(q) => void run(q, () => approveQuestion(q))}
                  onDecline={(q, reason) =>
                    void run(q, () => declineQuestion(q, reason))
                  }
                />
              );
            }
            return (
              <QuestionCard
                key={question.id}
                question={question}
                agentNames={agentNames}
                busy={busy}
                onApprove={(q) => void run(q, () => approveQuestion(q))}
                onDecline={(q, reason) =>
                  void run(q, () => declineQuestion(q, reason))
                }
              />
            );
          })
        )}
        {questions.length > 0 ? (
          <Text as="p" tone="subdued" variant="bodySm">
            Answered here or in the approvals inbox — same record, same
            decision.
          </Text>
        ) : null}
      </div>
    </section>
  );
}
